import { Controller } from '@hotwired/stimulus';
import { queueDisplaySave } from 'helpers/display_saves';

// Page-wide toggles for the goal cards' panels on a progress report: one button per panel
// (details / status / comments). A click opens that panel on every goal card when any
// card still has it closed, and closes it on every card otherwise. The panels are Bootstrap
// collapses; the state is applied directly (no animation) so a page-wide sweep never lands half
// way, and each card's own pill is kept in step (aria-expanded and the collapsed class). A master
// reads as pressed (aria-pressed) while every card shows its panel, and re-syncs after a card's
// own pill is used.
//
// What stands open is what the PDF prints, goal by goal: when the element carries a url value,
// every master click and every pill (the plugin's shown/hidden events) saves the state of each
// goal card on the page to the report (PATCH, JSON). The page has changed by then, so a save that
// does not go through (a session that ended, a change the report refused) is said on the page
// (the notice target) rather than left for the PDF to contradict. The payload names each card by
// its data-goals-report-goal id and each of its panels as open or closed. A master click also
// speaks for the panels a card does not have, so a goal that gains one later (a first comment,
// say) follows the master.
//
// Targets:
//   button - each master (data-goals-report-panels-panel-param = details|status|comments)
//   notice - the alert shown once a save has failed (hidden until then)
// Values:
//   url - where to save the state (absent when the viewer may not change it)
export default class extends Controller {
  static targets = ['button', 'notice'];
  static values = { url: String };

  connect() {
    this.onCollapseEvent = (event) => {
      if (!event.target.dataset || !event.target.dataset.panel) return;
      this.sync();
      this.save();
    };
    const $ = this.jquery();
    if ($) $(this.element).on('shown.bs.collapse hidden.bs.collapse', this.onCollapseEvent);
    this.sync();
  }

  disconnect() {
    const $ = this.jquery();
    if ($) $(this.element).off('shown.bs.collapse hidden.bs.collapse', this.onCollapseEvent);
  }

  // With nothing on the page to toggle (no goal has that panel yet) the master still flips, so
  // the choice of what to print can be made ahead of the content.
  toggle(event) {
    const { panel } = event.params;
    const panels = this.panels(panel);
    let open;
    if (panels.length) {
      open = panels.some((element) => !element.classList.contains('show'));
      panels.forEach((element) => this.apply(element, open));
    } else {
      const button = event.currentTarget;
      open = button.getAttribute('aria-pressed') !== 'true';
      this.press(button, open);
    }
    this.sync();
    this.save({ [panel]: open });
  }

  // Every goal card on the page: goal id => panel key => open. `spoken` adds the panels a master
  // just decided for every card, present on the card or not.
  state(spoken = {}) {
    const state = {};
    this.element.querySelectorAll('[data-goals-report-goal]').forEach((card) => {
      const panels = {};
      card.querySelectorAll('[data-panel]').forEach((panel) => {
        panels[panel.dataset.panel] = panel.classList.contains('show');
      });
      state[card.dataset.goalsReportGoal] = { ...panels, ...spoken };
    });
    return state;
  }

  // One at a time with the page's other display saves (helpers/display_saves), every card's panels
  // read when the save's turn comes, so it carries the page as it stands by then.
  save(spoken = {}) {
    if (!this.hasUrlValue || !this.urlValue) return;
    const url = this.urlValue;
    queueDisplaySave(() => {
      const token = document.querySelector('meta[name="csrf-token"]');
      return fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': token ? token.getAttribute('content') : '',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ panels: this.state(spoken) }),
      });
    })
      .then((response) => {
        // A redirect is the sign-in page: the session ended and nothing was saved.
        if (!response.ok || response.redirected) throw new Error(`Panels not saved (${response.status})`);
      })
      .catch(() => this.failed());
  }

  // The page and the report now differ; say so until the page is reloaded.
  failed() {
    this.noticeTargets.forEach((element) => {
      element.hidden = false;
    });
  }

  panels(key) {
    return Array.from(this.element.querySelectorAll(`[data-panel="${key}"]`));
  }

  apply(panel, open) {
    panel.classList.remove('collapsing');
    panel.classList.add('collapse');
    panel.classList.toggle('show', open);
    panel.style.height = '';
    if (!panel.id) return;
    this.element
      .querySelectorAll(
        `[data-toggle="collapse"][href="#${panel.id}"], [data-toggle="collapse"][data-target="#${panel.id}"]`
      )
      .forEach((pill) => {
        pill.setAttribute('aria-expanded', open ? 'true' : 'false');
        pill.classList.toggle('collapsed', !open);
      });
  }

  // A master with nothing to toggle on this page keeps whatever state it holds (rendered from the
  // saved choice, or flipped by a click); the others read from the panels.
  sync() {
    this.buttonTargets.forEach((button) => {
      const panels = this.panels(button.dataset.goalsReportPanelsPanelParam);
      if (panels.length === 0) return;
      this.press(
        button,
        panels.every((element) => element.classList.contains('show'))
      );
    });
  }

  press(button, pressed) {
    button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    button.classList.toggle('active', pressed);
  }

  jquery() {
    return typeof window !== 'undefined' && typeof window.$ === 'function' ? window.$ : null;
  }
}
